<template>
  <!-- The extension page's *only* call to action, wherever it appears on the
       page. One destination (the Chrome Web Store listing), one button, a label
       that names the browser you're actually reading this in.

       The width is fixed rather than fitted: the label is `Add to Chrome` in the
       prerendered HTML and becomes `Add to Vivaldi` (or `Get it on Chrome`) a
       tick after hydration, and a button that grows would push the line under it
       down — the same class of shift the homepage's headline and the Turnstile
       widget are already engineered around. -->
  <div :class="align === 'center' ? 'flex flex-col items-center' : ''">
    <BaseButton
      :href="EXTENSION_URL"
      :size="compact ? 'md' : 'lg'"
      :class="compact ? 'min-w-[9.5rem]' : 'min-w-[16rem]'"
      :aria-label="ariaLabel"
      @click="onClick"
    >
      <template #icon>
        <!-- The logo of the browser the label names. Height is fixed and the
             width follows the mark, so a wide logo can't stretch a tall one —
             and the button's min-width already absorbs the few px of difference
             between "Add to Chrome" and "Add to Vivaldi". -->
        <BrowserLogo :browser="logoBrowser" :class="compact ? 'h-4 w-auto' : 'h-5 w-auto'" />
      </template>
      {{ label }}
    </BaseButton>

    <p v-if="!compact" class="mt-3 text-sm text-gray-400" :class="align === 'center' ? 'text-center' : ''">
      {{ note }}
    </p>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import BaseButton from '~/components/atoms/BaseButton.vue'
import BrowserLogo from '~/components/atoms/BrowserLogo.vue'
import { useBrowserDetect } from '~/composables/useBrowserDetect'
import { trackEvent } from '~/composables/analytics'
import { EXTENSION_URL } from '~/utils/site'

const props = defineProps({
  // Where on the page this instance sits — hero, mid-page, closing. Rides along
  // on the event so the same button in three places stays one number that can
  // still be broken down.
  placement: { type: String, required: true },
  align: { type: String, default: 'left' },
  // Nav-bar sizing: the button on its own, without the line of small print
  // under it. Same button, same destination — the page still has one CTA.
  compact: { type: Boolean, default: false },
})

const { browser } = useBrowserDetect()

// Chromium forks all install from the Chrome Web Store, so they get their own
// name. Firefox and Safari can't install it at all: saying "Add to Firefox"
// there would be a lie, and saying "Add to Chrome" without explanation reads as
// a page that didn't notice — so the label stays honest and the line under it
// does the explaining.
const label = computed(() => (browser.value.chromium ? `Add to ${browser.value.name}` : 'Get it on Chrome'))

// On a browser that can't install it the button points at Chrome and says so,
// so the mark follows the destination rather than the reader.
const logoBrowser = computed(() => (browser.value.chromium ? browser.value.id : 'chrome'))

const ariaLabel = computed(() => `${label.value} — install the Colorize color palette extension`)

// Inside the price box the "free, no account" half is the panel's whole point
// and is already said twice above the button, so that placement gets the
// browser list on its own. The non-Chromium line is the same everywhere: it is
// the reason the button won't work for you, and that doesn't vary by placement.
const note = computed(() => {
  if (!browser.value.chromium) {
    return `Not available for ${browser.value.name} yet — the extension needs a Chromium browser (Chrome, Brave, Edge, Arc, Opera, Vivaldi).`
  }
  return props.placement === 'pricing'
    ? 'Works on Chrome, Brave, Edge, Arc, Opera and Vivaldi.'
    : 'Free forever, no account.'
})

// One event name across every placement, matching how the footers already
// report the same destination from two surfaces.
const route = useRoute()
const onClick = () => {
  try {
    trackEvent('Extension Install Clicked', {
      placement: props.placement,
      browser: browser.value.id,
      page: route.path,
    })
  } catch {}
}
</script>
